<script>
import Vue from 'vue'
import Component from 'vue-class-component'
import { mapGetters } from 'vuex'

import EventBus from '@/event-bus'
import { caseloadHelper } from '@/helpers/caseloadHelper'
import TableFilters from '@/components/admin/TableFilters'

@Component({
    components: {
        TableFilters
    },
    props: {
        teachersDisplayed: Array,
        selectedSchool: Object,
        selectedObservations: Array,
        componentsDisplayed: Array
    },
    computed: {
        ...mapGetters({
            userRole: "getUserRole",
            evaluators: "admin/getEvaluators",
            grades: "admin/getGradeLevels",
            subjects: "admin/getSubjects"
        }),
        filterTeacherList () {
            return this.filterList(this.teachersDisplayed)
        },
        filterObservationsList() {
            return this.filterList(this.selectedObservations)
        },
        filterComponentsList() {
            return this.filterList(this.componentsDisplayed)
        }
    }
})

export default class CaseloadTable extends Vue {
    tableDisplay = 'teachers'
    filters = caseloadHelper().filters
    experienceRanges = caseloadHelper().experienceRanges
    tipOptions = caseloadHelper().tipOptions
    
    clearFilters() {
        this.filters = caseloadHelper().filters
    }
    
    toggleDisplay(selection) {
        this.tableDisplay = selection
        this.clearFilters()
    }

    filterList(array) {
        const _filters = this.filters
        const _array = array

        const gradeSet = new Set(_filters.grades)
        const subjectSet = new Set(_filters.subjects)
        const evaluatorSet = new Set(_filters.evaluators)
        const experienceSet = new Set(_filters.experience)
        const tipSet = new Set(_filters.tipOptions)

        if (array === this.teachersDisplayed) {
            return _array.filter(item => {
                const gradeSet2 = new Set(item.grades)
                const subjectSet2 = new Set(item.subjects)
                const evaluatorSet2 = new Set(item.evaluators)
                const tipSet2 = new Set([item.flag_TIP])

                const gInt = caseloadHelper().intersection(gradeSet2, gradeSet)
                const sInt = caseloadHelper().intersection(subjectSet2, subjectSet)
                const eInt = caseloadHelper().intersection(evaluatorSet2, evaluatorSet)
                const tInt = caseloadHelper().intersection(tipSet2, tipSet)

                return (gInt.size > 0 || gradeSet.size === 0) 
                    && (sInt.size > 0 || subjectSet.size === 0) 
                    && (eInt.size > 0 || evaluatorSet.size === 0)
                    && (tInt.size > 0 || tipSet.size === 0)
            })
        } else {
            return _array.filter(item => {
                return (gradeSet.has(item.Grade_Level_Desc) || gradeSet.size === 0)
                    && (subjectSet.has(item.Subject_Desc) || subjectSet.size === 0)
                    && (caseloadHelper().experienceListed(item.Years_Of_Service, [...experienceSet]) || experienceSet.size === 0)
            })
        }
    }
    
    rectWidth(numerator, denominator) {
        return (numerator/denominator)*100 + '%'
    }

    componentsNeeded(teacher) {
        let missingComps = []
        for (let key in teacher.components) {
            if (!teacher.components[key].count) {
                missingComps.push(key)
            }
        }
        return missingComps.join(', ')
    }

    selectionDisplay(completedObservations, expectedObservations) {
        return expectedObservations || expectedObservations === 0
            ? `${completedObservations}/${expectedObservations}`
            : 'No selection made'
    }

    filterCount(observations, evaluator, option){
        return option ?
            observations.filter(observation => 
                observation.EvaluatorName === evaluator 
                && observation.MOTPTypeDescription.includes(option)).length 
            : observations.filter(observation => observation.EvaluatorName === evaluator).length 
    }

    componentMeasurement(evaluator, measurement) {
        const components = this.filterComponentsList.filter(component =>
            component.EvaluatorName === evaluator
        )
        const dualComponents = [23, 24, 30, 31, 32, 33]
        let componentCounts = caseloadHelper().componentCounts
        components.forEach(function(component) {
            if (component.Rating > 0) {
                if (dualComponents.includes(component.MOTPComponentID)) {
                    componentCounts.find(element => 
                        element['MOTPComponentID'].includes(component.MOTPComponentID)
                    ).count += 1
                } else {
                    componentCounts.find(element => 
                        element.MOTPComponentID === component.MOTPComponentID
                    ).count += 1
                }
            }
        })
        if (measurement === 'unrated') {
            return componentCounts.filter(element => element.count === 0)
                                    .map(element => element.component)
                                    .sort()
                                    .join(', ')
        } else {
            return Math.max(...componentCounts.map(item => item.count)) > 0
                ? componentCounts.filter(element => 
                        element.count === Math.max(...componentCounts.map(item => item.count))
                    ).map(element => element.component)
                    .join(', ')
                : ''
        }
    }

    filterTable(filter) {
        this.$ga.event({
            eventCategory: `Filter ${this.tableDisplay} Caseload table by ${filter}`,
            eventAction: this.selectedSchool.dbn,
            eventLabel: this.userRole,
        })
    }

    setTIPView() {
        return this.filters.tipOptions.length > 0
            ? this.filters.tipOptions = []
            : this.filters.tipOptions = [true]
    }
    
    showClearFilters() {
        for (let i = 0; i < Object.values(this.filters).length; i++) {
            if (Object.values(this.filters)[i].length > 0) {
                return true
                break
            }
        }
        return false
    }

    viewObservations() {
        EventBus.$emit('view_observations')
        this.$ga.event({
            eventCategory: 'Open Teacher\'s Observations (Caseload Table)',
            eventAction: this.selectedSchool.dbn,
            eventLabel: this.userRole
        })
    }
}
</script>

<template>
    <div class="caseload-table">
        <p id="role-view">
            View:  
            <span :class="{ active: tableDisplay === 'teachers' }"
                @click="toggleDisplay('teachers')">Teacher
            </span>
            <span :class="{ active: tableDisplay === 'evaluators' }"
                @click="toggleDisplay('evaluators')">Evaluator
            </span>
        </p>
        
        <transition name="fade" mode="out-in">
            <div v-if="tableDisplay === 'teachers'"
                key="teachers">
                <!-- <TableFilters :userRole="userRole"
                    :selectedSchool="selectedSchool"
                    :evaluators="evaluators" 
                    :grades="grades"
                    :subjects="subjects" /> -->
                <div class="filters">
                    <span>Filter By:</span>
                    <div class="filter">
                        <el-select multiple collapse-tags
                            v-model="filters.evaluators"
                            placeholder="Evaluator"
                            @change="filterTable('evaluator')">
                            <el-option 
                            v-for="item in evaluators" 
                            :key="item.name" 
                            :label="item.name" 
                            :value="item.name">
                            </el-option>
                        </el-select>
                    </div>
                    <div class="filter">
                        <el-select multiple collapse-tags 
                            v-model="filters.grades"
                            placeholder="Grade"
                            @change="filterTable('grade')">
                            <el-option
                                v-for="item in grades"
                                :key="item.name"
                                :label="item.name"
                                :value="item.name">
                            </el-option>
                        </el-select>
                    </div>
                    <div class="filter">
                        <el-select multiple collapse-tags
                            v-model="filters.subjects"
                            placeholder="Subject"
                            @change="filterTable('subject')">
                            <el-option 
                                v-for="item in subjects" 
                                :key="item.name" 
                                :label="item.name" 
                                :value="item.name">
                            </el-option>
                        </el-select>
                    </div>
                    <el-button @click="setTIPView"
                        :class="{ 'tip-teachers': this.filters.tipOptions.length > 0 }">
                        Teachers With a TIP
                    </el-button>
                    <el-button plain
                        v-if="showClearFilters()"
                        icon="el-icon-circle-close"
                        @click="clearFilters">
                        Clear Filters
                    </el-button>
                </div>
                
                <table class="teachers">
                    <tr>
                        <th>Teacher Name</th>
                        <th>Total Observation Count</th>
                        <th>Total<br>Informal</th>
                        <th>Total<br>Formal</th>
                        <th>Components Needed</th>
                    </tr>
                    <tr v-for="(teacher, index) in filterTeacherList" :key="index">
                        <router-link class="teacher-name"
                            :to="{name: 'observations', params: {id: teacher.EmployeeID}}"
                            tag="td"
                            @click.native="viewObservations">
                            {{ `${teacher.LastName}, ${teacher.FirstName}` }}
                        </router-link>
                        <td width="15%">
                            <svg width="70%" 
                                height="16"
                                v-if="teacher.expected_obs">
                                <rect :width="rectWidth(teacher.observation_N, teacher.expected_obs)"
                                    height="16" />
                            </svg>
                            <span>{{ selectionDisplay(teacher.observation_N, teacher.expected_obs) }}</span>
                        </td>
                        <td width="7%">{{ selectionDisplay(teacher.observation_N_informal, teacher.expected_obs_informal) }}</td>
                        <td width="7%">{{ selectionDisplay(teacher.observation_N_formal, teacher.expected_obs_formal) }}</td>
                        <td>{{ componentsNeeded(teacher) }}</td>
                    </tr>
                </table>
            </div>
        
            <div v-if="tableDisplay === 'evaluators'"
                key="evaluators">
                <div class="filters evaluators">
                    <span>Sort by:</span>
                    <div class="filter">
                        <el-select multiple collapse-tags 
                            v-model="filters.experience"
                            value-key="label"
                            placeholder="Teacher Experience in Years"
                            @change="filterTable('teacher experience')">
                            <el-option 
                                v-for="item in experienceRanges" 
                                :key="item.label" 
                                :label="item.label" 
                                :value="item">
                            </el-option>
                        </el-select>
                    </div>
                    <div class="filter">
                        <el-select multiple collapse-tags 
                            v-model="filters.grades"
                            placeholder="Grade"
                            @change="filterTable('grade')">
                            <el-option
                                v-for="item in grades"
                                :key="item.name"
                                :label="item.name"
                                :value="item.name">
                            </el-option>
                        </el-select>
                    </div>
                    <div class="filter">
                        <el-select multiple collapse-tags
                            v-model="filters.subjects"
                            placeholder="Subject"
                            @change="filterTable('subject')">
                            <el-option 
                            v-for="item in subjects" 
                            :key="item.name" 
                            :label="item.name" 
                            :value="item.name">
                            </el-option>
                        </el-select>
                    </div>
                    <el-button plain
                        v-if="showClearFilters()"
                        icon="el-icon-circle-close"
                        @click="clearFilters">
                        Clear Filters
                    </el-button>
                </div>
                <table class="evaluators">
                    <tr>
                        <th rowspan="2">Evaluator Name</th>
                        <th rowspan="2">Observation Count To-Date</th>
                        <th rowspan="2">Total<br>Informal</th>
                        <th rowspan="2">Total<br>Formal</th>
                        <th colspan="2">Components</th>
                    </tr>
                    <tr>
                        <th>Not Rated</th>
                        <th>Most Rated</th>
                    </tr>
                    <tr v-for="evaluator in selectedSchool.evaluators" :key="evaluator.id">
                        <td>{{ evaluator }}</td>
                        <td width="16%">{{ filterCount(filterObservationsList, evaluator) }}</td>
                        <td width="7%">{{ filterCount(filterObservationsList, evaluator, 'Informal') }}</td>
                        <td width="7%">{{ filterCount(filterObservationsList, evaluator, 'Formal') }}</td>
                        <td width="20%">{{ componentMeasurement(evaluator, 'unrated') }}</td>
                        <td width="20%">{{ componentMeasurement(evaluator, 'most rated') }}</td>
                    </tr>
                </table>
            </div>
        </transition>
    </div>
</template>

<style lang="scss" scoped>

.caseload-table {
    margin-top: 93px;
    p#role-view {
        font-size: 27px;
        margin: 0;
        padding: 1% 0;
        span {
            padding: 0.5%;
            width: 10%;
            border: 1px solid #979797;
            cursor: pointer;
            &:first-child {
                margin-left: 1%;
            }
            &.active {
                background: #e7dbee;
            }
        }
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity .75s;
    }

    .fade-enter, .fade-leave-to {
        opacity: 0;
    }

    .filters {
        font-family: 'museo_sans', sans-serif;
        display: flex;
        padding: 1em 0;
        margin-bottom: 1em;
        &.evaluators .filter:nth-of-type(1) {
            width: 250px;
        }
        span {
            color: #4a4a4a;
            font-size: 27px;
            margin-right: 2%;
        }
        .filter {
            display: flex;
            flex-direction: column;
            width: 191px;
            margin-right: 32px;
            &.view-by {
                margin-right: -40px;
            }
            span {
                margin-bottom: 5px;
                color: #4a4a4a;
                font-size: 14px;
            }
        }
        .school-filter {
            width: 350px;
            span {
                display: block;
                margin-bottom: 5px;
                color: #4a4a4a;
                font-size: 14px;
            }
        }
    }

    table {
        width: 91%;
        font-family: 'museo_sans';
        border: 1px solid #979797;
        border-collapse: collapse;
        th, td {
            font-size: 14px;
            font-weight: 500;
            text-align: left;
            padding: 0.5% 1%;
            border: 1px solid #979797;
        }
        td {
            svg {
                border: 1px solid #979797;
                margin-right: 4%;
                rect {
                    fill: #d8d8d8;
                }
            }
            span {
                vertical-align: top;
            }
            .teacher-name {
                text-decoration: none;
                color: inherit;
            }
        }
        td:first-child:hover{
            background-color: #e7dbee;
            cursor: pointer;
        }
        &.evaluators {
            tr:first-child th:last-child,
            tr:nth-child(2) th,
            tr:nth-child(n+3) td:nth-child(n+5) {
                text-align: center;
            }
        }
    }
}

</style>